import { type ReactNode, useMemo } from 'react';

import type { EmployeeStatus, JobHistoryLevel } from '@/common/graphql';
import { JobHistoryLevelLabels } from '@/common/types';
import { EmployeeStatusBadge } from '../badge';
import { Card } from '../card';
import { MaterialIcons } from '../material-icons';
import { EMPTY_DEFAULT } from '../table';
import { EmployeeHeaderLoader } from './EmployeeHeaderLoader';

interface Props {
  pictureUrl?: string | null;
  fullName?: string;
  employeeId: string;
  nameInLocalLanguage: string;
  company: string;
  department: string;
  jobTitle: string;
  jobLevel?: JobHistoryLevel;
  status?: EmployeeStatus;
  aside?: ReactNode;
}

export const EmployeeHeader = ({
  pictureUrl,
  fullName,
  employeeId,
  nameInLocalLanguage,
  company,
  department,
  jobTitle,
  jobLevel,
  status,
  aside,
}: Props) => {
  const details = useMemo(
    () =>
      [company, department, jobTitle, jobLevel ? JobHistoryLevelLabels[jobLevel] : '']
        .filter(v => v.length && v !== EMPTY_DEFAULT)
        .join(' - '),
    [company, department, jobTitle, jobLevel],
  );

  if (!fullName) {
    return <EmployeeHeaderLoader />;
  }

  return (
    <Card className="flex w-full flex-col items-start md:items-center justify-between md:flex-row gap-2">
      <div className="flex flex-1 items-start gap-x-4">
        <div className="shrink-0">
          {pictureUrl ? (
            <img alt={fullName} className="size-16" referrerPolicy="no-referrer" src={pictureUrl} />
          ) : (
            <MaterialIcons iconName="person" fill={true} fontSize="64px" className="size-16 text-gray-4" />
          )}
        </div>
        <div className="flex flex-col">
          <div>
            <div className="flex items-center gap-2">
              <div className="text-lg font-semibold leading-tight">
                {fullName}
                <span className="ml-2 text-gray-7">#{employeeId}</span>
              </div>
              {status && <EmployeeStatusBadge status={status} />}
            </div>
            {nameInLocalLanguage && <div className="leading-tight text-gray-7">{nameInLocalLanguage}</div>}
            <div className="text-xs mt-1">{details}</div>
          </div>
        </div>
      </div>
      {aside && <div className="w-full md:w-auto">{aside}</div>}
    </Card>
  );
};
